import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import type { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { makeProvider } from '@shared/form-fields/make-provider';
import { AbstractFormFieldComponent } from '../abstract-form-field/abstract-form-field.component';

@Component({
    selector: 'app-multi-email-input',
    template: `
        <mat-form-field appearance="outline">
            <mat-label *ngIf="label">
                {{ label }}
            </mat-label>
            <mat-chip-list
                #chipList
                aria-label="emails"
            >
                <mat-chip
                    *ngFor="let email of value"
                    (removed)="remove(email)"
                >
                    {{ email }}
                    <button matChipRemove>
                        <mat-icon>cancel</mat-icon>
                    </button>
                </mat-chip>
                <input
                    #input
                    placeholder="New email Address..."
                    [matChipInputFor]="chipList"
                    [matChipInputSeparatorKeyCodes]="separatorKeys"
                    [matChipInputAddOnBlur]="addOnBlur"
                    (matChipInputTokenEnd)="add($event)"
                />
            </mat-chip-list>
            <mat-hint *ngIf="value.length < maxItems && !errorMessages">
                Separate emails with a comma.
                {{ hint }}
            </mat-hint>
            <mat-hint *ngIf="value.length >= maxItems"> Maximum number of items has been reached</mat-hint>
            <mat-hint *ngIf="errorMessages">
                <mat-error *ngFor="let message of errorMessages">{{ message }}</mat-error>
            </mat-hint>
        </mat-form-field>
    `,
    styleUrls: ['./email-input.component.scss'],
    providers: [makeProvider(InputMultiEmailComponent)],
})
export class InputMultiEmailComponent extends AbstractFormFieldComponent<string[]> {
    @ViewChild('input') input: ElementRef<HTMLInputElement>;
    @Input() hint: string;
    @Input() label: string;
    @Input() maxItems?: number;
    @Input() name: string;
    @Input() placeholder: string;
    @Input() addOnBlur: boolean = true;
    @Input() appearance: MatFormFieldAppearance = 'outline';
    @Output() emailAdded = new EventEmitter<string>();
    @Output() emailNotAdded = new EventEmitter<string>();
    protected _emails: string[];
    @Input() set emails(value: string[]) {
        this.writeValue(this.value);
        this._emails = this.value;
    }

    get emails(): string[] {
        return this._emails;
    }

    readonly separatorKeys = [ENTER, COMMA, SEMICOLON] as const;

    private emailToLowercase(email: string): string {
        return email.toLowerCase();
    }

    private basicEmailValidation(email: string): boolean {
        const pattern = /^\S+@\S+\.\S+$/;
        return pattern.test(email.toLowerCase());
    }

    public add(event: MatChipInputEvent): void {
        const email = (event.value || '').trim();

        if (email) {
            if (!this.basicEmailValidation(email)) {
                this.remove(email);
            } else {
                const value = [...this.value, this.emailToLowercase(email)];
                this.writeValue(value);

            }
            this.dispatchChange();
        }
        event.chipInput!.clear();
    }

    public remove(email: string): void {
        const index = this.value.indexOf(email);

        if (index >= 0) {
            const value = [...this.value];
            value.splice(index, 1);
            this.writeValue(value);
        }
    }

    writeValue(value: string[]) {
        this._emails = value;
        super.writeValue(value);
    }
}
